// ./src/components/generic/FeatureGateContent/FeatureGateContent.tsx
//
// The "What you'd get" body of a locked page, written PER PAGE rather than per feature.
//
// ⚠ WHY PER PAGE. One feature code covers a whole module — `production` alone owns Budget,
// Rentals, Inventory, Travel, Map, Volunteers, Race timing and Purchase orders. The feature's own
// one-line description ("Budget, rentals, inventory, volunteers, travel, map") is therefore the
// same sentence on eight different screens, and says nothing about the one the host is looking at.
// A host who clicked Budget wants to know what BUDGET does.
//
// Copy lives in `i18n/locales/<lng>/plans.json` under `gates.<gateKey>`, keyed by the page's
// `gateKey` in `siteMap.ts` — so it is translatable, and so adding a gated page means writing its
// copy in one file rather than embedding prose in a component.
//
// ⚠ IT DEGRADES RATHER THAN BREAKS. A page with no copy yet falls back to the feature's generated
// description, which is generic but never wrong. A missing key must not produce an empty upgrade
// screen — that is the one outcome worse than a generic one.
import React from "react";
import { List, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { FEATURE_DESCRIPTIONS } from "@/config/featureCodes.generated";

export interface FeatureGateContentProps {
  /** The page's `gateKey` from the sitemap. Absent → the generic feature description. */
  gateKey?: string | null;
  /** Used for the fallback line when there is no per-page copy. */
  featureCode: string;
}

/**
 * Reads an array out of the namespace, tolerating a missing or mistyped key.
 *
 * ⚠ `returnObjects` hands back whatever is in the JSON — including the key itself as a STRING
 * when the key is absent. Rendering that would print `gates.budget.paragraphs` on the page, so the
 * shape is checked rather than trusted.
 */
const readList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0);
};

export const FeatureGateContent: React.FC<FeatureGateContentProps> = ({ gateKey, featureCode }) => {
  const { t } = useTranslation(["plans"]);

  const base = gateKey ? `plans:gates.${gateKey}` : null;

  const headline = base ? t(`${base}.headline`, { defaultValue: "" }) : "";
  const paragraphs = base ? readList(t(`${base}.paragraphs`, { returnObjects: true, defaultValue: [] })) : [];
  const bullets = base ? readList(t(`${base}.bullets`, { returnObjects: true, defaultValue: [] })) : [];

  const hasCopy = !!headline || paragraphs.length > 0 || bullets.length > 0;

  // ⚠ The generated feature description is the FLOOR, not the default — it only appears when this
  // page has no copy of its own. It is accurate but module-wide, so it reads oddly on a specific
  // page; that is a reason to write the copy, not a reason to render nothing.
  if (!hasCopy) {
    const fallback = FEATURE_DESCRIPTIONS[featureCode];

    if (!fallback) {
      return null;
    }

    return (
      <Text size="md" c="dimmed">
        {fallback}
      </Text>
    );
  }

  return (
    <Stack gap="md">
      {headline ? (
        <Text size="lg" fw={600}>
          {headline}
        </Text>
      ) : null}

      {paragraphs.map((paragraph) => (
        <Text key={paragraph} size="md" c="dimmed">
          {paragraph}
        </Text>
      ))}

      {bullets.length > 0 ? (
        <List
          spacing="xs"
          size="md"
          icon={
            <ThemeIcon color="teal" size={20} radius="xl">
              <IconCheck size={13} />
            </ThemeIcon>
          }
        >
          {bullets.map((bullet) => (
            <List.Item key={bullet}>{bullet}</List.Item>
          ))}
        </List>
      ) : null}
    </Stack>
  );
};
